import React, { useState } from 'react';
import { Box, Button, Stack, Typography, alpha } from '@mui/material';
import { CheckCircleOutline, ForwardToInboxOutlined } from '@mui/icons-material';
import useTimezone from '../../../hooks/useTimezone';
import type { AdmissionApplication } from '../../../types/admissions';
import ResendDocumentsDialog from '../dialogs/ResendDocumentsDialog';
import { NO_EMAIL_HINT } from '../decisionEmails';

export interface RejectedDocument {
  key: string;
  label: string;
  reason: string;
  /** When it was rejected. */
  at: string | null;
}

interface Props {
  app: AdmissionApplication;
  rejected: RejectedDocument[];
  /** The family's addresses; empty when none is on file. */
  to: string[];
  onSent: () => Promise<void> | void;
}

/**
 * Rejecting a document emails no one. Once staff are done reviewing, this
 * asks the family once for every rejected document — and afterwards says
 * when they were asked, until something new is rejected.
 */
const DocumentResendBar: React.FC<Props> = ({ app, rejected, to, onSent }) => {
  const { formatDateTime } = useTimezone();
  const [open, setOpen] = useState(false);
  const askedAt = app.resubmissionRequest?.at ?? null;
  const notAsked = rejected.filter(d => !askedAt || !d.at || new Date(d.at) > new Date(askedAt)).length;
  const n = rejected.length;

  const dialog = open && (
    <ResendDocumentsDialog
      applicationId={app._id}
      child={app.name}
      documents={rejected}
      to={to}
      onClose={() => setOpen(false)}
      onSent={async () => { setOpen(false); await onSent(); }}
    />
  );

  if (notAsked === 0) {
    return (
      <Stack direction="row" spacing={1} alignItems="center">
        <CheckCircleOutline sx={{ fontSize: 18, color: 'success.main' }} />
        <Typography variant="body2" color="text.secondary" sx={{ flex: 1, '& b': { color: 'text.primary', fontWeight: 600 } }}>
          Emailed{to.length ? <> <b>{to.join(', ')}</b></> : null} on {formatDateTime(askedAt!)}, asking to resend {n === 1 ? 'the rejected document' : `${n} documents`}.
        </Typography>
        {to.length > 0 && (
          <Button size="small" onClick={() => setOpen(true)} sx={{ textTransform: 'none', fontWeight: 600 }}>Email again</Button>
        )}
        {dialog}
      </Stack>
    );
  }

  return (
    <Box sx={(t) => ({
      display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', p: 1.5, pl: 2, borderRadius: 2.5,
      bgcolor: alpha(t.palette.primary.main, 0.06), border: `1px solid ${alpha(t.palette.primary.main, 0.2)}`,
    })}>
      <ForwardToInboxOutlined sx={{ color: 'primary.main' }} />
      <Box sx={{ flex: 1, minWidth: 200 }}>
        <Typography variant="body2" fontWeight={600}>
          {notAsked === 1 ? '1 rejected document' : `${notAsked} rejected documents`} not emailed yet
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {to.length
            ? <>When you’re done reviewing, send one email to <b>{to.join(', ')}</b> listing every rejected document with its reason.</>
            : `${NO_EMAIL_HINT} Or let them know another way.`}
        </Typography>
      </Box>
      {to.length > 0 && (
        <Button variant="contained" disableElevation size="small" onClick={() => setOpen(true)} sx={{ textTransform: 'none', fontWeight: 600 }}>
          Send email
        </Button>
      )}
      {dialog}
    </Box>
  );
};

export default DocumentResendBar;
